import type { GlbHierarchyNode, ModelAssetFile, ProjectModelSceneBinding, SelectionTarget } from '@puppycad/cad-core'

/**
 * 删除子树 payload 的存储层级。
 */
export type AssemblyDeletedSubtreePayloadStorageTier = 'opfs' | 'indexeddb'

/**
 * 删除子树 payload 引用。
 */
export interface AssemblyDeletedSubtreePayloadRef {
  /** payload 稳定 ID，用于 Undo/Redo 从冷存储取回场景快照。 */
  readonly payloadId: string
  /** 实际写入的浏览器存储层级。 */
  readonly storageTier: AssemblyDeletedSubtreePayloadStorageTier
}

/**
 * 删除子树 payload 中可序列化的模型资产文件描述。
 */
export interface AssemblyDeletedSubtreeModelAssetFileSnapshot {
  /** 文件在版本提交内的路径。 */
  readonly fileId: string
  /** 文件业务 UUID。 */
  readonly uuid: string
  /** 文件类型。 */
  readonly kind: string
  /** 下载 URL。 */
  readonly url: string
  /** 文件在项目模型加载流程中的职责。 */
  readonly role: ModelAssetFile['role']
}

/**
 * 删除子树 payload 中的文件字节快照。
 */
export interface AssemblyDeletedSubtreeFileSnapshot {
  /** 文件所属项目模型节点 key。 */
  readonly nodeKey: string
  /** 当前文件在项目模型加载中的职责。 */
  readonly role: ModelAssetFile['role']
  /** 可序列化的文件描述。 */
  readonly file: AssemblyDeletedSubtreeModelAssetFileSnapshot
  /** 文件字节的 base64 表示。 */
  readonly bytesBase64: string
}

/**
 * 删除子树中的单棵场景对象快照。
 */
export interface AssemblyDeletedSubtreeObjectSnapshot {
  /** 被移除对象根 UUID。 */
  readonly objectId: string
  /** 原父对象 UUID；根挂在 scene 下时记录 scene 的 UUID。 */
  readonly parentObjectId: string | null
  /** 原父对象 children 中的位置，用于 Undo 时恢复顺序。 */
  readonly parentIndex: number
  /** 历史 payload 兼容字段；新删除记录不再依赖 Object3D JSON 恢复模型运行时。 */
  readonly objectJson?: unknown
  /** Object3D.metadata 不属于 three 原生 toJSON，需要额外按 UUID 存储。 */
  readonly metadataByObjectId: Record<string, unknown>
}

/**
 * 删除子树中的 hierarchy 快照。
 */
export interface AssemblyDeletedSubtreeHierarchySnapshot {
  /** 被移除 hierarchy 根对象 UUID。 */
  readonly objectId: string
  /** 原父 hierarchy 对象 UUID。 */
  readonly parentObjectId: string | null
  /** 原父 hierarchy children 中的位置。 */
  readonly parentIndex: number
  /** 被移除的 hierarchy 子树。 */
  readonly node: GlbHierarchyNode
}

/**
 * 装配组件删除子树 payload。
 */
export interface AssemblyDeletedSubtreePayload {
  /** payload 稳定 ID。 */
  readonly payloadId: string
  /** 根装配节点 key。 */
  readonly rootNodeKey: string
  /** 本次删除的项目模型节点 key。 */
  readonly deletedNodeKey: string
  /** 被删除节点及后代 nodeKey。 */
  readonly deletedNodeKeys: readonly string[]
  /** 被删除节点在 ProjectModelLoadResult 中的场景绑定。 */
  readonly sceneBindings: readonly ProjectModelSceneBinding[]
  /** 被删除对象对应的选择索引。 */
  readonly selectionTargetsByObjectId: Record<string, SelectionTarget>
  /** 被删除的可挂载场景对象根快照。 */
  readonly objectSnapshots: readonly AssemblyDeletedSubtreeObjectSnapshot[]
  /** 被删除的设计树 hierarchy 子树快照。 */
  readonly hierarchySnapshots: readonly AssemblyDeletedSubtreeHierarchySnapshot[]
  /** 被删除子树的节点包文件快照；Undo 时用它重新生成 Topo/BRep/场景对象。 */
  readonly fileSnapshots?: readonly AssemblyDeletedSubtreeFileSnapshot[]
  /** 写入时间。 */
  readonly createdAt: number
}

type AssemblyDeletePayloadIndexedDBRecord = AssemblyDeletedSubtreePayload

const ASSEMBLY_DELETE_PAYLOAD_DATABASE_NAME = 'PuppyCADAssemblyDeletePayloads'
const ASSEMBLY_DELETE_PAYLOAD_DATABASE_VERSION = 1
const ASSEMBLY_DELETE_PAYLOAD_STORE_NAME = 'payloads'
const ASSEMBLY_DELETE_PAYLOAD_OPFS_DIRECTORY = 'assembly-delete-payloads'

/**
 * 把字节数组编码为 base64。
 *
 * @param bytes 文件字节。
 * @returns base64 字符串。
 */
function encodeBytesToBase64(bytes: Uint8Array): string {
  const chunkSize = 0x8000
  let binary = ''

  for (let offset = 0; offset < bytes.byteLength; offset += chunkSize) {
    const chunk = bytes.subarray(offset, Math.min(offset + chunkSize, bytes.byteLength))
    binary += String.fromCharCode(...chunk)
  }

  return btoa(binary)
}

/**
 * 把 base64 解码为字节数组。
 *
 * @param base64 base64 字符串。
 * @returns 文件字节。
 */
function decodeBase64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }

  return bytes
}

/**
 * 创建可序列化的模型资产文件快照。
 *
 * @param file 模型资产文件。
 * @returns 可写入 payload 的文件快照。
 */
function createModelAssetFileSnapshot(file: ModelAssetFile): AssemblyDeletedSubtreeModelAssetFileSnapshot {
  return {
    fileId: file.fileId,
    uuid: file.uuid,
    kind: file.kind,
    url: file.url,
    role: file.role,
  }
}

/**
 * 从 payload 文件快照恢复模型资产文件描述。
 *
 * @param snapshot 文件快照。
 * @returns 模型资产文件。
 */
export function restoreAssemblyDeletedSubtreeModelAssetFile(
  snapshot: AssemblyDeletedSubtreeModelAssetFileSnapshot,
): ModelAssetFile {
  return {
    fileId: snapshot.fileId,
    uuid: snapshot.uuid,
    kind: snapshot.kind,
    url: snapshot.url,
    role: snapshot.role,
  }
}

/**
 * 创建删除子树文件字节快照。
 *
 * @param input 文件、节点与字节输入。
 * @returns payload 可序列化文件快照。
 */
export function createAssemblyDeletedSubtreeFileSnapshot(input: {
  readonly nodeKey: string
  readonly role: ModelAssetFile['role']
  readonly file: ModelAssetFile
  readonly bytes: Uint8Array
}): AssemblyDeletedSubtreeFileSnapshot {
  return {
    nodeKey: input.nodeKey,
    role: input.role,
    file: createModelAssetFileSnapshot(input.file),
    bytesBase64: encodeBytesToBase64(input.bytes),
  }
}

/**
 * 解码删除子树文件快照中的字节。
 *
 * @param snapshot 文件快照。
 * @returns 文件字节。
 */
export function decodeAssemblyDeletedSubtreeFileSnapshotBytes(
  snapshot: AssemblyDeletedSubtreeFileSnapshot,
): Uint8Array {
  return decodeBase64ToBytes(snapshot.bytesBase64)
}

/**
 * 将 IndexedDB request 包装为 Promise。
 *
 * @param request IndexedDB 请求。
 * @returns 请求结果。
 */
function requestToPromise<TResult>(request: IDBRequest<TResult>): Promise<TResult> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB 请求失败'))
  })
}

/**
 * 等待 IndexedDB 事务结束。
 *
 * @param transaction IndexedDB 事务。
 */
function waitForTransaction(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB 事务失败'))
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB 事务已中止'))
  })
}

/**
 * 打开删除子树 payload IndexedDB。
 *
 * @returns IndexedDB 数据库连接。
 */
function openAssemblyDeletePayloadDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(
      ASSEMBLY_DELETE_PAYLOAD_DATABASE_NAME,
      ASSEMBLY_DELETE_PAYLOAD_DATABASE_VERSION,
    )

    request.onupgradeneeded = () => {
      const database = request.result

      if (!database.objectStoreNames.contains(ASSEMBLY_DELETE_PAYLOAD_STORE_NAME)) {
        database.createObjectStore(ASSEMBLY_DELETE_PAYLOAD_STORE_NAME, { keyPath: 'payloadId' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('打开装配删除 payload IndexedDB 失败'))
  })
}

/**
 * 在删除子树 payload IndexedDB 上运行事务。
 *
 * @param mode 事务模式。
 * @param work 事务工作函数。
 * @returns 工作函数结果。
 */
async function runAssemblyDeletePayloadIndexedDBTransaction<TResult>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => Promise<TResult> | TResult,
): Promise<TResult> {
  const database = await openAssemblyDeletePayloadDatabase()

  try {
    const transaction = database.transaction([ASSEMBLY_DELETE_PAYLOAD_STORE_NAME], mode)
    const store = transaction.objectStore(ASSEMBLY_DELETE_PAYLOAD_STORE_NAME)
    const result = await work(store)

    await waitForTransaction(transaction)

    return result
  } finally {
    database.close()
  }
}

/**
 * 判断当前浏览器是否可用 OPFS。
 *
 * @returns 可用时返回 true。
 */
function canUseOPFS(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.storage?.getDirectory === 'function'
}

/**
 * 生成删除子树 payload ID。
 *
 * @returns payload ID。
 */
function createAssemblyDeletedSubtreePayloadId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

/**
 * 读取或创建删除子树 OPFS 目录。
 *
 * @returns OPFS 目录句柄。
 */
async function resolveAssemblyDeletePayloadOPFSDirectory(): Promise<FileSystemDirectoryHandle> {
  const root = await navigator.storage.getDirectory()

  return await root.getDirectoryHandle(ASSEMBLY_DELETE_PAYLOAD_OPFS_DIRECTORY, { create: true })
}

/**
 * 写入 OPFS payload。
 *
 * @param payload payload 内容。
 */
async function writeAssemblyDeletePayloadToOPFS(payload: AssemblyDeletedSubtreePayload): Promise<void> {
  const directory = await resolveAssemblyDeletePayloadOPFSDirectory()
  const fileHandle = await directory.getFileHandle(`${payload.payloadId}.json`, { create: true })
  const writable = await fileHandle.createWritable()

  try {
    await writable.write(JSON.stringify(payload))
  } finally {
    await writable.close()
  }
}

/**
 * 从 OPFS 读取 payload。
 *
 * @param payloadId payload ID。
 * @returns payload 内容。
 */
async function readAssemblyDeletePayloadFromOPFS(payloadId: string): Promise<AssemblyDeletedSubtreePayload> {
  const directory = await resolveAssemblyDeletePayloadOPFSDirectory()
  const fileHandle = await directory.getFileHandle(`${payloadId}.json`)
  const file = await fileHandle.getFile()

  return JSON.parse(await file.text()) as AssemblyDeletedSubtreePayload
}

/**
 * 清理 OPFS 删除子树 payload 目录。
 */
async function clearAssemblyDeletePayloadOPFS(): Promise<void> {
  if (!canUseOPFS()) {
    return
  }

  const root = await navigator.storage.getDirectory()

  try {
    await root.removeEntry(ASSEMBLY_DELETE_PAYLOAD_OPFS_DIRECTORY, { recursive: true })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'NotFoundError') {
      return
    }

    throw error
  }
}

/**
 * 写入 IndexedDB payload。
 *
 * @param payload payload 内容。
 */
async function writeAssemblyDeletePayloadToIndexedDB(payload: AssemblyDeletedSubtreePayload): Promise<void> {
  await runAssemblyDeletePayloadIndexedDBTransaction('readwrite', (store) => {
    store.put(payload satisfies AssemblyDeletePayloadIndexedDBRecord)
  })
}

/**
 * 从 IndexedDB 读取 payload。
 *
 * @param payloadId payload ID。
 * @returns payload 内容。
 */
async function readAssemblyDeletePayloadFromIndexedDB(payloadId: string): Promise<AssemblyDeletedSubtreePayload> {
  return await runAssemblyDeletePayloadIndexedDBTransaction('readonly', async (store) => {
    const payload = await requestToPromise(store.get(payloadId)) as AssemblyDeletePayloadIndexedDBRecord | undefined

    if (!payload) {
      throw new Error(`装配删除 payload 缺失：${payloadId}`)
    }

    return payload
  })
}

/**
 * 清理 IndexedDB 删除子树 payload。
 */
async function clearAssemblyDeletePayloadIndexedDB(): Promise<void> {
  if (typeof indexedDB === 'undefined') {
    return
  }

  await runAssemblyDeletePayloadIndexedDBTransaction('readwrite', (store) => {
    store.clear()
  })
}

/**
 * 写入删除子树 payload。
 *
 * 设计说明：
 * 1. 优先使用 OPFS 存储 Object3D JSON，避免大型几何快照进入内存历史栈；
 * 2. OPFS 不可用时退回 IndexedDB，保持 Undo/Redo 能力不因浏览器能力缺失而中断；
 * 3. 返回值只包含引用，后续历史快照只保存这份轻量引用。
 *
 * @param payloadWithoutId 未分配 ID 的 payload。
 * @returns payload 引用。
 */
export async function writeAssemblyDeletedSubtreePayload(
  payloadWithoutId: Omit<AssemblyDeletedSubtreePayload, 'payloadId' | 'createdAt'>,
): Promise<AssemblyDeletedSubtreePayloadRef> {
  const payload: AssemblyDeletedSubtreePayload = {
    ...payloadWithoutId,
    payloadId: createAssemblyDeletedSubtreePayloadId(),
    createdAt: Date.now(),
  }

  if (canUseOPFS()) {
    await writeAssemblyDeletePayloadToOPFS(payload)

    return {
      payloadId: payload.payloadId,
      storageTier: 'opfs',
    }
  }

  await writeAssemblyDeletePayloadToIndexedDB(payload)

  return {
    payloadId: payload.payloadId,
    storageTier: 'indexeddb',
  }
}

/**
 * 读取删除子树 payload。
 *
 * @param ref payload 引用。
 * @returns payload 内容。
 */
export async function readAssemblyDeletedSubtreePayload(
  ref: AssemblyDeletedSubtreePayloadRef,
): Promise<AssemblyDeletedSubtreePayload> {
  if (ref.storageTier === 'opfs') {
    return await readAssemblyDeletePayloadFromOPFS(ref.payloadId)
  }

  return await readAssemblyDeletePayloadFromIndexedDB(ref.payloadId)
}

/**
 * 清理当前浏览器中的装配删除 payload。
 */
export async function clearAssemblyDeletedSubtreePayloadStorage(): Promise<void> {
  await Promise.all([
    clearAssemblyDeletePayloadOPFS(),
    clearAssemblyDeletePayloadIndexedDB(),
  ])
}
